import { useCallback, useEffect } from 'react';

import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { useAudioPlayerStreamingContext } from '../context/AudioPlayerStreamingProvider';
import { setActiveContent } from '../state/AudioPlayerState';
import type { PlaylistItemData } from '../ui/playlist/PlaylistItem';

import type { usePlaylistAutoPlayQuery$data } from './__generated__/usePlaylistAutoPlayQuery.graphql';
import { useAudioPlayerStore } from './useAudioPlayerStore';
import { useContextMenuActions } from './useContextMenuActions';

type UsePlaylistAutoPlayProps = {
	playlistItems: PlaylistItemData[];
	currentItemId: string | undefined;
};

//this had to be duplicated in order to satisfy relay unused data in the same file requirements
const covertDataToPlaylist = (data: usePlaylistAutoPlayQuery$data) => {
	const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];

	return tracks
		.filter((track: any) => {
			const mainContent = track.mainContent;
			const hasMainContent = !!mainContent;
			const hasValidStatus = mainContent?.status === 'current' || mainContent?.status === 'draft';
			return hasMainContent && hasValidStatus;
		})
		.map((track: any) => {
			const mainContent = track.mainContent;
			const supportingContent = track.supportingContent;
			const creator = mainContent.history?.createdBy || mainContent.history?.ownedBy;
			const avatarUrl = creator?.profilePicture?.path || undefined;

			return {
				id: mainContent.id,
				title: mainContent.title || undefined,
				trackType: track.type,
				author: creator?.displayName || undefined,
				avatarUrl,
				spaceKey: mainContent.space?.key || '',
				contentType: mainContent.type || '',
				contentId: mainContent.id,
				mainContent: {
					ari: track.mainSource,
					link: mainContent.links?.base + mainContent.links?.webui || '',
				},
				supportingContent: supportingContent.map((content: any) => ({
					ari: content.ari,
					link: content.links?.base + content.links?.webui || '',
				})),
			};
		});
};

export const usePlaylistAutoPlay = ({
	playlistItems,
	currentItemId,
}: UsePlaylistAutoPlayProps): void => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { cloudId } = useAudioPlayerStore();
	const { removeTrack } = useContextMenuActions(cloudId);
	const environment = useRelayEnvironment();

	const { sendTrackEvent } = usePlatformInstrumentation();

	const playNextTrack = useCallback(
		async (data?: any) => {
			let playlistData = playlistItems;
			if (data?.confluence_getPlaylist?.tracks?.nodes) {
				playlistData = covertDataToPlaylist(data);
			}

			if (playlistData.length === 0) {
				return;
			}

			const currentIndex = playlistData.findIndex((item) => item.contentId === currentItemId);

			// Get the current item to remove it
			const currentItem = currentIndex !== -1 ? playlistItems[currentIndex] : null;
			// Remove the finished track if we have the necessary data
			if (currentItem && currentItem.mainContent.ari) {
				try {
					const mainSource = currentItem.mainContent.ari;
					const supportingSources =
						currentItem.supportingContent?.map((content) => content.ari) || [];
					const trackType = currentItem.trackType;

					await removeTrack(mainSource, supportingSources, trackType);
					// eslint-disable-next-line no-unused-vars
				} catch (_error) {
					// Silently ignore errors - continue to next track even if removal fails
					// leaving this for analytics instrumentation
				}
			}

			let nextTrack: PlaylistItemData;
			let nextIndex = currentIndex + 1;

			if (currentIndex === -1 || currentIndex === playlistData.length - 1) {
				nextTrack = playlistItems[0];
				nextIndex = 0;
			} else if (playlistData.length === 0) {
				sendTrackEvent({
					eventType: 'track',
					action: 'ended',
					actionSubject: 'autoplay',
					actionSubjectId: 'playlist',
					source: 'playlist',
					attributes: {
						reason: 'ended',
					},
				});
				return; //we're out of content to play
			} else {
				nextTrack = playlistData[currentIndex + 1];
			}

			setActiveContent({
				contentId: nextTrack?.contentId,
				spaceKey: nextTrack?.spaceKey || '',
				contentType: nextTrack?.contentType || '',
				title: nextTrack?.title,
				author: nextTrack?.author || '',
				avatarUrl: nextTrack?.avatarUrl || '',
				supportingContent: nextTrack?.supportingContent || [],
				interimSupportingContent: undefined,
				mainContentLink: nextTrack?.mainContent?.link,
			});

			await fetchAudio()
				.catch((error) => {
					sendTrackEvent({
						eventType: 'track',
						action: 'error',
						actionSubject: 'autoplay',
						actionSubjectId: 'playlist',
						source: 'playlist',
						attributes: {
							error,
						},
					});
				})
				.then(() => {
					sendTrackEvent({
						eventType: 'track',
						action: 'success',
						actionSubject: 'autoplay',
						actionSubjectId: 'playlist',
						source: 'playlist',
						attributes: {
							previousContentId: currentItem?.contentId,
							nextContentId: nextTrack?.contentId,
							previousPosition: currentIndex,
							nextPosition: nextIndex,
							totalPlaylistSize: playlistData.length,
							cloudId,
						},
					});
				});
		},
		[currentItemId, playlistItems, fetchAudio, removeTrack, cloudId, sendTrackEvent],
	);

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio) {
			return;
		}

		const onEnded = () => {
			fetchQuery<any>(
				environment,
				graphql`
					query usePlaylistAutoPlayQuery($cloudId: ID!) {
						confluence_getPlaylist(cloudId: $cloudId) {
							tracks {
								nodes {
									id
									type
									mainSource
									mainContent {
										id
										title
										type
										status
										space {
											key
										}
										history {
											createdBy {
												displayName
												profilePicture {
													path
												}
											}
											ownedBy {
												displayName
												profilePicture {
													path
												}
											}
										}
										links {
											webui
											base
										}
									}
									supportingContent {
										ari
										links {
											webui
											base
										}
									}
								}
							}
						}
					}
				`,
				{ cloudId },
				{ fetchPolicy: 'network-only' },
			).subscribe({
				next: (data) => {
					playNextTrack(data);
				},
				error: () => {
					playNextTrack();
				},
			});
		};

		audio.addEventListener('ended', onEnded);

		return () => {
			audio.removeEventListener('ended', onEnded);
		};
	}, [audioRef, playNextTrack, cloudId, environment]);
};
